/**
 * System events that can be emitted by workflows
 * These events can trigger other workflows or be consumed by external systems
 */
export enum SystemEvent {
  // Email ingestion events
  EMAIL_INGESTED = "EMAIL_INGESTED",
  EMAIL_ROUTED_EMAIL_RULE = "EMAIL_ROUTED_EMAIL_RULE",
  EMAIL_ROUTED_AR_REMITTANCE = "EMAIL_ROUTED_AR_REMITTANCE",

  // AP invoice events
  // Emitted by the `create_ap_invoice_draft` step when it persists a NEW
  // DRAFT invoice from an extraction. Not emitted when the step resolves to an
  // existing invoice (same-source rerun, thread/dedup match, arbitration
  // update) — those paths never create a draft. Payload includes the invoice
  // entity plus `referenceIds` so a split handling workflow can fetch
  // Famous PO/SO/TO/load records without re-running extraction.
  AP_INVOICE_DRAFT_CREATED = "AP_INVOICE_DRAFT_CREATED",
  AP_INVOICE_ACCEPTED = "AP_INVOICE_ACCEPTED",
  // Emitted by exception evaluation (match node and Re-evaluate Exceptions)
  // when the pass leaves exceptions open, or resolves the last open ones.
  // A pass that finds nothing and changes nothing does not emit. Workflows
  // subscribe with an event trigger; `outcome` is `open` or `cleared`.
  AP_INVOICE_EXCEPTIONS_EVALUATED = "AP_INVOICE_EXCEPTIONS_EVALUATED",

  // AP payment events
  // Emitted once a payment collects its last required approval and moves
  // in_review → approved (single or bulk approve). Published after the
  // approval commits; not emitted when a bulk scheduled approval is rolled
  // back. Carries the payment's creator so a workflow can notify them.
  PAYMENT_APPROVED = "PAYMENT_APPROVED",

  // Task events
  TASK_CREATED = "TASK_CREATED",
  // Emitted on every task status transition (pending → in_progress, reopen,
  // escalation to needs_input, terminal transitions, …). Published from the
  // task service layer so every path — oRPC handlers, workflow steps, the
  // agent task loop — emits exactly once per transition. Terminal transitions
  // additionally emit TASK_COMPLETED (the coarse "task concluded" event).
  TASK_STATUS_CHANGED = "TASK_STATUS_CHANGED",
  // Emitted when any task reaches a terminal status (completed | failed |
  // cancelled). Counterpart to TASK_CREATED; carries the task type key, anchor
  // entity, and outcome so a workflow can re-trigger off it. Also still carries
  // the OMS v3 approval-decision fields (category + flat subject ids +
  // decision) published by the event-driven approval gates, which predate the
  // generalized task-lifecycle payload. Single re-trigger event for both
  // approvals and corrective actions.
  TASK_COMPLETED = "TASK_COMPLETED",

  // Agent resource events
  // Emitted by the backend when the A2A operator positively observed a stored
  // credential being rejected (`runError.code === "credential_rejected"`) and
  // Node stopped the holding run without pausing its resources. Published from
  // `handleRejectedCredential` — the single choke point that revokes the
  // holding run's lease (which aborts the in-flight run) and stamps the
  // credential. Payload carries names and ids only, never a secret value nor
  // the operator's own failure prose.
  CREDENTIAL_REJECTED = "CREDENTIAL_REJECTED",

  // AR sales order events
  SALES_ORDER_APPROVED = "SALES_ORDER_APPROVED",

  // Procurement: a draft purchase order was created (buyer Confirm) and held for
  // role/$-authority approval. Triggers the per-division PO approval workflow,
  // which gates on the PO amount + initiator and releases the held draft to the
  // outbound push on approval. See docs/procurement/po-approval-workflow.md.
  DRAFT_PURCHASE_ORDER_CREATED = "DRAFT_PURCHASE_ORDER_CREATED",

  // OMS v3 draft-sales-order routing/handoff events
  // Routing org → target org (cross-org): the routing workflow classified the
  // target org and hands the draft off (carries targetOrganizationId).
  DRAFT_SALES_ORDER_ROUTED = "DRAFT_SALES_ORDER_ROUTED",
  // Target org, intra-org: the target org's extract/line-breakout workflow has
  // populated the draft and it is ready for the approval-chain workflow.
  DRAFT_SALES_ORDER_READY = "DRAFT_SALES_ORDER_READY",
  // Routing org: classify_organization could not confidently match a target org;
  // emitted so an admin-notification workflow can alert the Gently team to
  // manually assign (→ reassign_draft_sales_order).
  DRAFT_SALES_ORDER_UNCLASSIFIED = "DRAFT_SALES_ORDER_UNCLASSIFIED",

  // AR approval events (discount and order-level approvals)
  APPROVAL_NEEDED = "APPROVAL_NEEDED",

  // Discounts v2: a discount group (exception price list / bulk batch /
  // discount request) was submitted for approval. Triggers the group-approval
  // workflow, whose Approval step decides it and flips the group's elements
  // active on approve.
  DISCOUNT_GROUP_SUBMITTED = "DISCOUNT_GROUP_SUBMITTED",

  // Discounts v2, routing org → target org (cross-org): the routing workflow
  // classified an inbound email's document(s) as a Discount Request and
  // matched the selling org; hands the email off so the target org's
  // event-triggered discount-request-ingestion workflow extracts it and
  // creates the discount group there (carries targetOrganizationId).
  DISCOUNT_REQUEST_DOCUMENT_ROUTED = "DISCOUNT_REQUEST_DOCUMENT_ROUTED",

  // AR remittance intake: a remittance document from a non-email source
  // (portal, bank memo, EDI, manual) was submitted through
  // RemittanceIntakeService.submit(). Never published on the event bus:
  // submit() starts the "AR Remittance Document Ingestion" workflow directly at
  // its event trigger (one run per document); the event type only gives that
  // trigger its typed output sockets.
  AR_REMITTANCE_DOCUMENT_SUBMITTED = "AR_REMITTANCE_DOCUMENT_SUBMITTED",

  // AR remittance portal fetch: fetch a customer's remittances from its portal
  // and submit each one. Starts the org's "AR Remittance Portal Fetch"
  // workflow whose trigger filters on the customer.
  AR_REMITTANCE_PORTAL_FETCH_REQUESTED = "AR_REMITTANCE_PORTAL_FETCH_REQUESTED",

  // AR check deposits: a file (a check scan, a remittance, or a check with its
  // stub) was dropped into a check deposit. Never published on the event bus:
  // the deposit service starts the org's configured deposit file workflow
  // directly at its event trigger (one run per file); the event type only
  // gives that trigger its typed output sockets.
  AR_DEPOSIT_FILE_SUBMITTED = "AR_DEPOSIT_FILE_SUBMITTED",

  // Rebate CSV transform (workflow started via API with session + artifact context)
  REBATE_CSV_TRANSFORM_STARTED = "REBATE_CSV_TRANSFORM_STARTED",

  // Pricing SPA request entity created (Module 1 CRUD — triggers the workflow DAG)
  SPA_REQUEST_SUBMITTED = "SPA_REQUEST_SUBMITTED",

  // Workbooks: a person's Workbook Run reached a terminal status (completed or
  // failed; `status` says which, like TASK_COMPLETED). Published from the Run
  // lifecycle's one terminal transition. Runs a Run Workbook node or an agent
  // tool started do not publish it: they report back to their caller, and a
  // workflow reacting to its own Workbook's completion would loop.
  WORKBOOK_RUN_COMPLETED = "WORKBOOK_RUN_COMPLETED",

  // Slack webhook received (Events API callbacks, interactivity payloads,
  // slash commands). Only emitted when the server has Slack credentials
  // configured AND the org has installed the Slack integration; hidden
  // from trigger pickers otherwise.
  SLACK_WEBHOOK_RECEIVED = "SLACK_WEBHOOK_RECEIVED",
}
